import { lazyLoadView } from '@utils/LazyLoad'
import { FEATURES, SEARCH_GROUPS } from '@constants/Text'
import { ehEscritorioInternoAawz } from '@modules/solicitacoes/utils/escritorioInterno'

// Gate dos fluxos P1 (assessor) do módulo — a visão EXTERNA que o escritório CLIENTE usa: profissional
// + funcionalidade Solicitações. SEM only_aawz: o escritório cliente (não-2976) precisa ver/usar a
// visão externa. Quem decide interna×externa é o redirect (redirectSolicitacoesRoot) + o gate 2976 da
// área de Operação AAWZ.
const CHILD_META = {
  permission: ['profissional'],
  can_show: FEATURES.SOLICITACOES,
}

// Gate da aba Consultor Estratégico (spec-008). Por decisão do dono do produto
// (2026-07-26) a persona NÃO tem flag própria: quem tem o módulo Solicitações tem o
// consultor. O gate é o mesmo do módulo, mantido como função para preservar o ponto de
// extensão — se um dia voltar a haver flag dedicada, muda só aqui.
// `show` pode chegar vazio no boot (antes da carga de parâmetros) — daí o default `{}`.
export const checkPermissionConsultor = (opts) => {
  const show = (opts && opts.show) || {}
  return Boolean(show[FEATURES.SOLICITACOES])
}

// Rota da aba do Consultor: CHILD_META (profissional + funcionalidade do módulo) MAIS o gate
// da persona. Os dois gates são cumulativos de propósito — a aba mora dentro de Solicitações,
// então quem não tem o módulo também não tem o consultor.
const CONSULTOR_META = {
  ...CHILD_META,
  customPermission: checkPermissionConsultor,
}

// Escritório executor da Operação AAWZ (Interno AAWZ, 2976 em prod) — é AAWZ-only.
// Alias público mantido p/ compatibilidade (o meta + os testes importam este nome);
// delega ao helper canônico `ehEscritorioInternoAawz` (fonte ÚNICA da detecção,
// compartilhada com a Comunicação e o settings + alinhada ao escritorio_interno_guard).
export const ehEscritorioOperacaoAawz = (user) => ehEscritorioInternoAawz(user)

// Gate das rotas de OPERAÇÃO AAWZ: profissional + funcionalidade Solicitações + escritório executor
// (2976). SEM only_aawz (IS_INTERNAL) — como o 2976 já é AAWZ-only, o gate por escritório basta e
// deixa o front consistente com o backend. customPermission é avaliado pelo guard global do router.
const OPERACAO_AAWZ_META = {
  permission: ['profissional'],
  customPermission: ({ user }) => ehEscritorioOperacaoAawz(user),
  can_show: FEATURES.SOLICITACOES,
}

// Ao abrir /solicitacoes, o escritório executor AAWZ (2976, e o de teste 83) NÃO fica mais no Hub —
// decisão P0.10 (src/modules/ws/pending-tasks.md:244-284, spec-002 US-8): a fila de trabalho do
// backoffice passou a morar dentro do Wealth Service. Por isso o redirect manda o backoffice para
// /wealth-service/backoffice/fila; os demais escritórios (parceiros) seguem para a visão-geral do
// próprio Hub de Solicitações, sem mudança. As rotas de Operação AAWZ abaixo continuam existindo e
// intocadas (Hub intocado) — só a raiz do módulo passou a redirecionar o executor para fora dele.
// require lazy do store evita ciclo de carga (routes é avaliado no boot do router, antes do store
// montar).
export const redirectSolicitacoesRoot = () => {
  const store = require('@state/store').default
  const getters = (store && store.getters) || {}
  const user = getters['user/currentUser'] || {}
  return ehEscritorioOperacaoAawz(user)
    ? '/wealth-service/backoffice/fila'
    : '/solicitacoes/visao-geral'
}

// GAP G-3 (QA E2E, spec-002-fila-backoffice-ws): a decisão acima NÃO pode ser um `redirect`
// estático (função) — o vue-router 3 resolve `redirect: fn` dentro do `match()`, que roda ANTES
// do guard global `beforeEach` (src/router/index.js). Em navegação a frio (refresh, link direto,
// bookmark, busca global) o Vuex ainda não hidratou `user/currentUser` nesse ponto: o getter
// devolve `{}`, `ehEscritorioOperacaoAawz({})` é `false` e o redirect trava TODO MUNDO em
// `/solicitacoes/visao-geral` — inclusive o escritório backoffice (2976/83), que deveria ir para
// `/wealth-service/backoffice/fila` (US-8). A decisão é tomada uma única vez e nunca reavaliada.
//
// O fix reusa o MESMO mecanismo já usado por `applyClienteFpRedirectGuard`/
// `applyAawzIntelligenceRedirectGuard` em `src/router/index.js` (RF-A12): o guard global
// `beforeEach` já faz `if (user.loading) await P.getCurrentUser()` ANTES de deixar a navegação
// prosseguir. `beforeEnter` (guard por rota) só é invocado pelo vue-router DEPOIS que todos os
// guards globais `beforeEach` chamaram `next()` — ou seja, depois dessa hidratação. Trocar
// `redirect` por `beforeEnter` não exige nenhuma mudança no guard global: basta mover a leitura
// do store para o momento em que o vue-router já garante o usuário carregado.
// GAP G-5 (QA E2E iteração 2, spec-002-fila-backoffice-ws) — REGRESSÃO do fix acima (G-3,
// commit 5406923f2). O vue-router 3 invoca o `beforeEnter` de um RouteRecord para TODA
// navegação cujo `matched` inclua esse record — não só quando ele é o destino final. Como
// este guard é o `beforeEnter` do record PAI (`/solicitacoes`), ele roda de novo quando a
// navegação que ele mesmo disparou via `next(redirectSolicitacoesRoot())` aterrissa numa
// FILHA dele (ex.: `/solicitacoes/visao-geral` — o pai continua em `matched` da filha).
// Sem distinguir os dois casos, o guard decidia o destino de novo e chamava
// `next(mesmoPath)`, o router navegava de novo para a mesma filha, o `beforeEnter` do pai
// rodava de novo — reentrância infinita (`RangeError: Maximum call stack size exceeded`) e
// TELA EM BRANCO para todo escritório NÃO-backoffice que abrisse `/solicitacoes` a frio
// (refresh, link direto, busca global). O ramo backoffice (2976/83) escapava porque seu
// destino (`/wealth-service/backoffice/fila`) fica FORA da árvore de `/solicitacoes` — por
// isso o QA da iteração 1 não pegou o bug: só a segunda passagem (filha → o próprio
// beforeEnter do pai de novo) reproduz a reentrância, e G-3 só cobria a primeira.
//
// Fix: só decide destino quando `routeTo` É a raiz do módulo em si — para qualquer filha
// (inclusive a que ele mesmo escolheu), apenas libera a navegação com `next()`. Usa
// `routeTo.name` (resolvido pelo vue-router a partir do record casado) em vez de
// `routeTo.path`: `name` já vem normalizado, imune a barra final (`/solicitacoes/`) e a
// query string — uma comparação por `path` precisaria tratar as duas coisas à parte.
export const solicitacoesRootBeforeEnter = (routeTo, routeFrom, next) => {
  const to = routeTo || {}
  const isNavigatingToRootItself = to.name === 'solicitacoes'
  if (!isNavigatingToRootItself) {
    next()
    return
  }
  next(redirectSolicitacoesRoot())
}

export default [
  {
    path: '/solicitacoes',
    name: 'solicitacoes',
    beforeEnter: solicitacoesRootBeforeEnter,
    meta: {
      permission: ['profissional'],
      can_show: FEATURES.SOLICITACOES,
      search: {
        title: 'Solicitações',
        description: 'Abertura e acompanhamento de solicitações operacionais',
        keywords: [
          'solicitações',
          'catálogo',
          'pedidos',
          'jornada',
          'atendimento',
        ],
        icon: 'clipboard-text',
        group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
      },
    },
    component: () =>
      lazyLoadView(
        import('@modules/solicitacoes/views/nav-bar/solicitacoes-nav-bar.vue')
      ),
    children: [
      {
        path: 'catalogo',
        name: 'solicitacoes.catalogo',
        meta: {
          ...CHILD_META,
          search: {
            title: 'Catálogo de Solicitações',
            description: 'Catálogo de tipos de solicitação disponíveis',
            keywords: ['catálogo', 'solicitações', 'tipos', 'serviços'],
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
          },
        },
        component: () =>
          lazyLoadView(
            import('@modules/solicitacoes/views/catalogo/view-catalogo.vue')
          ),
      },
      {
        path: 'catalogo/criar/:idTipo',
        name: 'solicitacoes.catalogo.criar',
        meta: CHILD_META,
        component: () =>
          lazyLoadView(
            import(
              '@modules/solicitacoes/views/criar/view-criar-solicitacao.vue'
            )
          ),
      },
      {
        path: 'visao-geral',
        name: 'solicitacoes.visao-geral',
        meta: {
          ...CHILD_META,
          search: {
            title: 'Minha Jornada — Solicitações',
            description: 'Visão geral e andamento das suas solicitações',
            keywords: ['visão geral', 'jornada', 'andamento', 'solicitações'],
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
          },
        },
        component: () =>
          lazyLoadView(
            import('@modules/solicitacoes/views/jornada/view-jornada.vue')
          ),
      },
      {
        path: 'minhas-solicitacoes',
        name: 'solicitacoes.minhas-solicitacoes',
        meta: {
          ...CHILD_META,
          search: {
            title: 'Minhas Solicitações',
            description: 'Lista das solicitações abertas por você',
            keywords: [
              'minhas solicitações',
              'lista',
              'acompanhamento',
              'status',
            ],
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/solicitacoes/views/solicitacoes-atuais/view-solicitacoes-atuais.vue'
            )
          ),
      },
      {
        path: 'minhas-solicitacoes/:id',
        name: 'solicitacoes.minhas-solicitacoes.detalhe',
        meta: CHILD_META,
        component: () =>
          lazyLoadView(
            import(
              '@modules/solicitacoes/views/solicitacao-detalhe/view-solicitacao-detalhe.vue'
            )
          ),
      },
      {
        path: 'comunicacao',
        name: 'solicitacoes.comunicacao',
        meta: {
          ...CHILD_META,
          search: {
            title: 'Comunicação — Solicitações',
            description: 'Mensagens e comunicação sobre solicitações',
            keywords: ['comunicação', 'mensagens', 'chat', 'solicitações'],
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/solicitacoes/views/comunicacao/view-comunicacao.vue'
            )
          ),
      },
      {
        path: 'consultor',
        name: 'solicitacoes.consultor',
        meta: CONSULTOR_META,
        component: () =>
          lazyLoadView(
            import(
              '@modules/solicitacoes/views/consultor/view-consultor-estrategico.vue'
            )
          ),
      },
    ],
  },
  {
    // Área da OPERAÇÃO AAWZ — visão CROSS-OFFICE das solicitações que os escritórios estão abrindo.
    // Shell próprio com 3 abas (Visão Geral 'Fila de Trabalho' + Calendário de prazos + Comunicação/
    // atendente), separado do fluxo do escritório. Gate: profissional + funcionalidade + escritório
    // executor 2976 (OPERACAO_AAWZ_META).
    path: '/solicitacoes/operacao-aawz',
    name: 'solicitacoes.operacao-aawz',
    redirect: '/solicitacoes/operacao-aawz/visao-geral',
    meta: {
      ...OPERACAO_AAWZ_META,
      search: {
        title: 'Operação AAWZ — Solicitações',
        description: 'Visão cross-office das solicitações dos escritórios',
        keywords: [
          'operação aawz',
          'fila',
          'solicitações',
          'atendimento interno',
        ],
        group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
      },
    },
    component: () =>
      lazyLoadView(
        import(
          '@modules/solicitacoes/views/operacao-aawz/operacao-aawz-nav-bar.vue'
        )
      ),
    children: [
      {
        path: 'visao-geral',
        name: 'solicitacoes.operacao-aawz.visao-geral',
        meta: {
          ...OPERACAO_AAWZ_META,
          search: {
            title: 'Fila de Trabalho — Operação AAWZ',
            description: 'Fila de solicitações a atender na operação AAWZ',
            keywords: [
              'fila de trabalho',
              'operação aawz',
              'solicitações',
              'atendimento',
            ],
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/solicitacoes/views/operacao-aawz/view-operacao-aawz-visao-geral.vue'
            )
          ),
      },
      {
        // Calendário dos PRAZOS das solicitações (reusa CalendarView + os dados da fila).
        path: 'calendario',
        name: 'solicitacoes.operacao-aawz.calendario',
        meta: {
          ...OPERACAO_AAWZ_META,
          search: {
            title: 'Calendário de Prazos — Operação AAWZ',
            description: 'Calendário de prazos das solicitações da operação',
            keywords: ['calendário', 'prazos', 'operação aawz', 'solicitações'],
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/solicitacoes/views/operacao-aawz/view-operacao-aawz-calendario.vue'
            )
          ),
      },
      {
        // Tarefas — réplica do board /task/board/list (sidebar + filtros + Lista/Kanban) embutida;
        // o clique numa tarefa abre o detalhe (TaskDetailModal). A view popula/limpa o store `tarefas`.
        path: 'tarefas',
        name: 'solicitacoes.operacao-aawz.tarefas',
        meta: OPERACAO_AAWZ_META,
        component: () =>
          lazyLoadView(
            import(
              '@modules/solicitacoes/views/operacao-aawz/view-operacao-aawz-tarefas.vue'
            )
          ),
      },
      {
        // Comunicação (atendente AAWZ responde os escritórios) — reusa a view-comunicacao, que
        // detecta o modo RESPONDER pelo escritório executor (2976).
        path: 'comunicacao',
        name: 'solicitacoes.operacao-aawz.comunicacao',
        meta: {
          ...OPERACAO_AAWZ_META,
          search: {
            title: 'Comunicação — Operação AAWZ',
            description: 'Atendimento e comunicação com os escritórios',
            keywords: [
              'comunicação',
              'atendimento',
              'operação aawz',
              'mensagens',
            ],
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/solicitacoes/views/comunicacao/view-comunicacao.vue'
            )
          ),
      },
      {
        // Detalhe interno — PÁGINA (o shell esconde o NavBarNavigation aqui; tem 'Voltar' próprio).
        path: 'detalhe/:id',
        name: 'solicitacoes.operacao-aawz.detalhe',
        meta: OPERACAO_AAWZ_META,
        component: () =>
          lazyLoadView(
            import(
              '@modules/solicitacoes/views/operacao-aawz/view-operacao-aawz-detalhe.vue'
            )
          ),
      },
    ],
  },
]
